// WHAT: Calorie Badge Component (Atom)
// WHY:  Displays calorie count with goal comparison and color-coded status
// DOMAIN: Health → Yazio
// DEPENDENCIES: Shared utils, CalorieBadgeProps type
// RISKS:
//   - NOTE: Color coding based on percentage thresholds (90-100% = yellow, >100% = red)
//   - NOTE: Percentage calculation assumes goal > 0 (returns 0 if goal is 0 or negative)

import type { CalorieBadgeProperties } from "../../types";

import { cn } from "@/shared/utils";

/**
 * Badge size variant type
 */
type BadgeSize = "lg" | "md" | "sm";

/**
 * WHAT:
 *   Badge component displaying calorie count with optional goal comparison.
 *
 * WHY:
 *   Provides consistent calorie display with visual status indicators (color-coded).
 *   Used throughout Yazio components for calorie tracking.
 *
 * PROPS:
 *   - calories: number — Current calorie count
 *   - goal: number — Calorie goal
 *   - size?: 'sm' | 'md' | 'lg' — Badge size variant (default: 'md')
 *   - showProgress?: boolean — Whether to show goal and percentage (default: true)
 *   - className?: string — Additional CSS classes
 *
 * BEHAVIOR:
 *   - Calculates percentage: (calories / goal) * 100
 *   - Color coding: red if over goal, yellow if 90-100%, green otherwise
 *   - Displays calories rounded to nearest integer
 *   - Shows goal and percentage if showProgress is true
 *   - Size variants affect padding and text size
 *
 * RISKS:
 *   - NOTE: Percentage calculation returns 0 if goal <= 0
 *   - NOTE: Color thresholds are hardcoded (90% and 100%)
 *   - NOTE: Values are rounded (may lose precision)
 *
 * @param root0
 *
 * @param root0.calories
 *
 * @param root0.goal
 *
 * @param root0.size
 *
 * @param root0.showProgress
 *
 * @param root0.className
 *
 * @returns {JSX.Element} Calorie badge component with progress indicator
 *
 */
export function CalorieBadge({
  calories,
  className,
  goal,
  showProgress = true,
  size = "md",
}: Readonly<CalorieBadgeProperties>) {
  const percentage: number = goal > 0 ? (calories / goal) * 100 : 0;
  const isOverGoal = calories > goal;
  const isNearGoal = percentage >= 90 && percentage <= 100;

  const sizeClasses: Record<BadgeSize, string> = {
    lg: "text-base px-4 py-2",
    md: "text-sm px-3 py-1.5",
    sm: "text-xs px-2 py-1",
  };

  const getColorClasses = () => {
    if (isOverGoal) {
      return "bg-red-900/20 border-red-500/20 text-red-400";
    } else if (isNearGoal) {
      return "bg-yellow-900/20 border-yellow-500/20 text-yellow-400";
    } else {
      return "bg-green-900/20 border-green-500/20 text-green-400";
    }
  };

  // size is guaranteed to be "lg" | "md" | "sm" due to default value
  const sizeKey: BadgeSize = size as BadgeSize;
  const sizeClass: string = sizeClasses[sizeKey];

  return (
    <div
      className={cn(
        "inline-flex items-center rounded-lg border font-medium",
        sizeClass,
        getColorClasses(),
        className,
      )}
    >
      <span className="font-semibold">{Math.round(calories)}</span>
      {showProgress ? (
        <>
          <span className="mx-1 text-slate-400">/</span>
          <span className="text-slate-300">{Math.round(goal)}</span>
          <span className="ml-1 text-xs text-slate-400">({Math.round(percentage)}%)</span>
        </>
      ) : null}
    </div>
  );
}
